import React, { useEffect, useState } from 'react'

// Hooks
import { useHistory } from 'react-router'
import { useSchedule, useUser } from '../../hooks'
import { usePopover } from '../../hooks/usePopover'
import { useStyles } from '../../styles'

// Helpers
import { isAccountManager, resolveRoute, Routes } from '../../helpers'

// Models
import { Schedule } from '../../models'
import { ScheduleRow } from './types'

// Components
import {
    FormControlLabel,
    IconButton,
    Popover,
    Switch,
    TextField,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import { ScheduleRowComponent } from './ScheduleRow'
import { Container, HeadCell, SimpleTable } from '../../components'
import { Publish } from '@material-ui/icons'
import { UploadWorkorderCSVModal } from '../../components/UploadWorkorderCSVModal'
import { ScheduleForm } from '../ScheduleHome/ViewComponents/ScheduleForm'
import { DateTime } from 'luxon'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'

export const ScheduleListModal = () => {
    const {
        scheduleList,
        updateSchedule,
        syncBedStatuses,
        getUnitReport,
        createSchedule,
    } = useSchedule()
    const theme = useTheme()
    const classes = useStyles()
    const history = useHistory()
    const { workspaceUser } = useUser()

    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    const userList = useSelector((state: RootState) => state.user.userList)
    const inspectionTypeList = useSelector(
        (state: RootState) => state.aptConfig.inspectionTypeList,
    )

    const [showArchive, setShowArchive] = useState(false)
    const [titleSearchValue, setTitleSearchValue] = useState('')

    const [uploadCSVModalOpen, setUploadCSVModalOpen] = useState(false)
    const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(
        null,
    )
    const { isOpen, anchorEl, handleOpen, handleClose } = usePopover<Schedule>()

    const [color, setColor] = useState(
        editingSchedule?.color ?? theme.palette.primary.main,
    )

    useEffect(() => {
        setColor(editingSchedule?.color ?? theme.palette.primary.main)
    }, [editingSchedule])

    // Table setup
    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const cellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 400,
        width: '1px',
    }

    const barLabelStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        height: 20,
        fontWeight: 300,
    }

    const barStyle: React.CSSProperties = {
        marginTop: 5,
        marginBottom: 5,
    }

    const headCells: HeadCell<ScheduleRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'nameAndId',
            label: 'Schedule',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'budget',
            label: 'Budget',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'startDate',
            label: 'Start',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'endDate',
            label: 'End',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'assignments',
            label: 'Assignments',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'inspection',
            label: 'Inspection',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'center',
            id: 'progress',
            label: 'Progress',
            style: headCellStyle,
        },
    ]

    const createScheduleRow = (schedule: Schedule): ScheduleRow => {
        const endDateSort = schedule.end_date ? schedule.end_date : -1

        const nameAndIdComponent = (
            <Container direction="column">
                <span
                    style={{
                        ...typographyStyle(theme.typography.body1),
                        fontWeight: 700,
                    }}
                >
                    {schedule.name}
                </span>
                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        fontWeight: 300,
                    }}
                >
                    {schedule.schedule_id}
                </span>
            </Container>
        )

        return {
            nameAndId: { value: nameAndIdComponent, sortValue: schedule.name },
            budget: {
                value: schedule.baseline_budget,
                sortValue: schedule.baseline_budget,
            },
            startDate: {
                value: schedule.start_date,
                sortValue: schedule.start_date,
            },
            endDate: { value: schedule.end_date, sortValue: endDateSort },
            assignments: {
                value: schedule.assigned_count,
                sortValue: schedule.assigned_count,
            },
            inspection: {
                value: schedule.inspection
                    ? schedule.inspection_name ||
                      inspectionList?.find(
                          (inspection) => inspection.id === schedule.inspection,
                      )?.name ||
                      `Inspection ${schedule.inspection}`
                    : '',
                sortValue: schedule.inspection
                    ? schedule.inspection_name ||
                      inspectionList?.find(
                          (inspection) => inspection.id === schedule.inspection,
                      )?.name ||
                      `Inspection ${schedule.inspection}`
                    : '',
            },
            schedule: { value: schedule, sortValue: -1 },
        }
    }
    // End Table Setup

    const schedulesToRender = scheduleList.filter((sch) => {
        const archiveValid =
            (showArchive && !sch.active) || (!showArchive && sch.active)

        let titleValid = true

        if (titleSearchValue !== '') {
            titleValid = sch.name
                .toLowerCase()
                .startsWith(titleSearchValue.toLowerCase())
        }

        return archiveValid && titleValid
    })

    return (
        <Container
            style={{
                padding: theme.spacing(2),
                flexDirection: 'column',
                height: 'calc(100vh - 104px)',
                width: 'calc(100vw - 184px)',
            }}
            flex={1}
            // style={{ flexDirection: 'column'}}
        >
            <Container
                alignItems="center"
                style={{ marginBottom: theme.spacing(1) }}
            >
                <span
                    style={{
                        ...typographyStyle(theme.typography.h5),
                        fontWeight: 700,
                        margin: theme.spacing(0, 2, 0, 0),
                    }}
                >
                    Schedules
                </span>

                <span
                    style={{
                        ...typographyStyle(theme.typography.body2),
                        fontWeight: 300,
                        margin: theme.spacing(0, 2, 0, 0),
                    }}
                >{`${schedulesToRender.length} Schedules`}</span>

                <div style={{ backgroundColor: 'red', flex: 1 }} />

                {isAccountManager(workspaceUser) && (
                    <Tooltip title="Upload Workorder CSV sheet">
                        <IconButton
                            onClick={() =>
                                setUploadCSVModalOpen(!uploadCSVModalOpen)
                            }
                        >
                            <Publish />
                        </IconButton>
                    </Tooltip>
                )}

                <FormControlLabel
                    control={
                        <Switch
                            value={showArchive}
                            color="primary"
                            onChange={() => setShowArchive(!showArchive)}
                        />
                    }
                    labelPlacement="start"
                    label="Archived"
                    style={{ marginRight: theme.spacing(2) }}
                />

                <TextField
                    value={titleSearchValue}
                    onChange={(e) => setTitleSearchValue(e.target.value)}
                    label="Search"
                />
            </Container>
            <SimpleTable<ScheduleRow>
                rows={schedulesToRender.map((sch) => createScheduleRow(sch))}
                render={(row, index) =>
                    ScheduleRowComponent(
                        {
                            row: row,
                            classes: classes,
                            theme: theme,
                            barLabelStyle: barLabelStyle,
                            cellStyle: cellStyle,
                            barStyle: barStyle,
                            workspaceUser: workspaceUser,
                            setEditFormOpen: (
                                open: boolean,
                                event?: React.MouseEvent<HTMLElement>,
                            ) => {
                                if (open && event) {
                                    setEditingSchedule(row.schedule.value)

                                    handleOpen(event, row.schedule.value)
                                } else {
                                    handleClose()
                                    setEditingSchedule(null)
                                }
                            },
                            onClickRow: () => {
                                let route =
                                    Routes.apartmentScheduleArchivedDetail
                                if (row.schedule.value.active) {
                                    route = Routes.apartmentScheduleDetail
                                }
                                history.push(
                                    resolveRoute(
                                        route,
                                        ':id',
                                        row.schedule.value.id,
                                    ),
                                )
                            },
                        },
                        index,
                    )
                }
                orderByDefault="startDate"
                headCells={headCells}
            />

            <UploadWorkorderCSVModal
                open={uploadCSVModalOpen}
                scheduleList={scheduleList.filter((sch) => sch.active)}
                onClose={() => setUploadCSVModalOpen(false)}
            />

            {/* Edit Form Popover */}
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={() => {
                    handleClose()
                    setEditingSchedule(null)
                }}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <Container
                    style={{
                        minWidth: 400,
                        maxWidth: 400,
                        minHeight: 600,
                        maxHeight: 600,
                    }}
                >
                    <ScheduleForm
                        theme={theme}
                        schedule={editingSchedule}
                        loading={false}
                        color={color}
                        initialStartDate={DateTime.now()}
                        apartment={workspaceUser?.active_workspace}
                        inspectionList={inspectionList ?? []}
                        setColor={setColor}
                        onClose={() => {
                            handleClose()
                            setEditingSchedule(null)
                        }}
                        createSchedule={createSchedule}
                        updateSchedule={updateSchedule}
                        syncBedStatuses={syncBedStatuses}
                        getUnitReport={getUnitReport}
                        userList={userList}
                        inspectionTypeList={inspectionTypeList ?? []}
                    />
                </Container>
            </Popover>
        </Container>
    )
}
